import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { CountryStateServiceModule } from '@vendasta/country-state';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyFrequencyModule } from '@vendasta/galaxy/frequency';
import { GalaxyI18NModule } from '@vendasta/galaxy/i18n';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { GalaxyPopoverModule } from '@vendasta/galaxy/popover';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ConfirmationModule, VaFilterModule, VaMaterialTableModule, VaStencilsModule } from '@vendasta/uikit';
import { FilterModule } from '@vendasta/va-filter2';
import { RecaptchaFormsModule, RecaptchaModule } from 'ng-recaptcha';
import { BillingErpUrlService } from './billing-erp-url.service';
import { ChargeInvoiceDialogComponent } from './charge-invoice-dialog/charge-invoice-dialog.component';
import { EditContactInfoDialogComponent } from './contact-information/edit-contact-info-dialog.component';
import { SubsidiaryWarningDialogComponent } from './contact-information/subsidiary-warning-dialog/subsidiary-warning-dialog.component';
import { RetailDiscountDialogComponent } from './discount/add-discount-modal/retail-discount-dialog.component';
import { BillingUiI18nModule } from './i18n/billing-ui-i18n.module';
import { PaymentListComponent } from './payment-list/payment-list.component';
import { EditPaymentMethodDialogComponent } from './payment-method/edit-payment-method-dialog.component';
import { PaymentMethodInfoComponent } from './payment-method/payment-method-info.component';
import { PriceDisplayComponent } from './price-display/price-display.component';
import { DunningDialogComponent } from './purchase/dunning-dialog/dunning-dialog.component';
import { PurchaseItemListComponent } from './purchase/purchase-item-list/purchase-item-list.component';
import { CanVoidPurchasePipe } from './purchase/purchase-list/can-void-purchase.pipe';
import { PurchaseListComponent } from './purchase/purchase-list/purchase-list.component';
import { RetryPurchaseDialogComponent } from './purchase/retry-purchase-dialog/retry-purchase-dialog.component';
import { VoidPurchaseDialogComponent } from './purchase/void-purchase-dialog/void-purchase-dialog.component';
import { RefundDialogComponent } from './refund-dialog/refund_dialog.component';
import { RefundListComponent } from './refund-list/refund-list.component';
import { SalesCreditNoteListComponent } from './sales-credit-note-list/sales-credit-note-list.component';
import { SalesInvoiceListComponent } from './sales-invoice-list/sales-invoice-list.component';
import { CdkDetailRowDirective } from './shared/cdk-detail-row.directive';
import { DiscountPipe, PricePipe, ToStringPipe } from './shared/pipes';
import { PriceColumnStyleDirective } from './shared/price-column-style.directive';
import { DiscountDisplayComponent } from './simple-price-display/discount-display/discount-display.component';
import { SimplePriceDisplayComponent } from './simple-price-display/simple-price-display.component';
import { StairstepDialogComponent } from './simple-price-display/stairstep-price-display/stairstep-dialog/stairstep-dialog.component';
import { StairstepPriceDisplayComponent } from './simple-price-display/stairstep-price-display/stairstep-price-display.component';
import { StripeUrlService } from './stripe-url.service';
import { StripeService } from './stripe.service';
import { ChangeFrequencyDialogComponent } from './subscription-list/change-frequency-dialog/change-frequency.component';
import { ChangePriceDialogComponent } from './subscription-list/change-price-dialog/change-price-dialog.component';
import { ChangeRenewalDialogComponent } from './subscription-list/change-renewal-dialog copy/change-renewal.component';
import { SubscriptionListComponent } from './subscription-list/subscription-list.component';
import { BillingStrategyPipe } from './utils/pipes/billing-strategy.pipe';
import { CurrencyWithoutUSDPipe } from './utils/pipes/currency-without-usd.pipe';
import { FrequencyPipe } from './utils/pipes/frequency.pipe';
import { PriceInDollarsPipe } from './utils/pipes/price-in-dollars.pipe';
import { ResolveCurrencyCodePipe } from './utils/pipes/resolve-currency-code.pipe';
import { TaxDescriptionPipe } from './utils/pipes/tax-description.pipe';
import { VariablePriceInputComponent } from './variable-price-input/variable-price-input.component';

const DIALOG_COMPONENTS = [
  ChargeInvoiceDialogComponent,
  RefundDialogComponent,
  RetryPurchaseDialogComponent,
  VoidPurchaseDialogComponent,
  DunningDialogComponent,
  EditPaymentMethodDialogComponent,
  EditContactInfoDialogComponent,
  ChangeFrequencyDialogComponent,
  ChangeRenewalDialogComponent,
  SubsidiaryWarningDialogComponent,
];

@NgModule({
  declarations: [
    SalesInvoiceListComponent,
    PaymentListComponent,
    SalesCreditNoteListComponent,
    PurchaseListComponent,
    PurchaseItemListComponent,
    PaymentMethodInfoComponent,
    PriceDisplayComponent,
    StairstepPriceDisplayComponent,
    VariablePriceInputComponent,
    SimplePriceDisplayComponent,
    StairstepDialogComponent,
    ...DIALOG_COMPONENTS,
    PricePipe,
    ToStringPipe,
    DiscountPipe,
    CdkDetailRowDirective,
    RefundListComponent,
    SubscriptionListComponent,
    BillingStrategyPipe,
    ChangePriceDialogComponent,
    CurrencyWithoutUSDPipe,
    PriceInDollarsPipe,
    ResolveCurrencyCodePipe,
    DiscountDisplayComponent,
    TaxDescriptionPipe,
    CanVoidPurchasePipe,
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    FrequencyPipe,
    RouterModule,
    MatInputModule,
    MatIconModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatDialogModule,
    MatDatepickerModule,
    ConfirmationModule,
    MatGridListModule,
    MatSelectModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatAutocompleteModule,
    TranslateModule,
    RecaptchaModule,
    RecaptchaFormsModule,
    CountryStateServiceModule,
    VaFilterModule,
    FilterModule,
    VaMaterialTableModule,
    VaStencilsModule,
    MatRippleModule,
    MatDividerModule,
    GalaxyFrequencyModule,
    GalaxyPipesModule,
    GalaxyPopoverModule,
    BillingUiI18nModule,
    GalaxyAlertModule,
    GalaxyFormFieldModule,
    GalaxyBadgeModule,
    GalaxyButtonLoadingIndicatorModule,
    GalaxyI18NModule,
    PriceColumnStyleDirective,
    RetailDiscountDialogComponent,
  ],
  providers: [
    SnackbarService,
    StripeUrlService,
    BillingErpUrlService,
    StripeService,
    PricePipe,
    CurrencyWithoutUSDPipe,
  ],
  exports: [
    SalesInvoiceListComponent,
    PaymentListComponent,
    RefundListComponent,
    SalesCreditNoteListComponent,
    PurchaseListComponent,
    PurchaseItemListComponent,
    PaymentMethodInfoComponent,
    EditPaymentMethodDialogComponent,
    EditContactInfoDialogComponent,
    SubscriptionListComponent,
    PricePipe,
    DiscountPipe,
    FrequencyPipe,
    BillingStrategyPipe,
    PriceDisplayComponent,
    VariablePriceInputComponent,
    SimplePriceDisplayComponent,
    TranslateModule,
    CurrencyWithoutUSDPipe,
    PriceInDollarsPipe,
    ResolveCurrencyCodePipe,
    DiscountDisplayComponent,
    TaxDescriptionPipe,
    RetailDiscountDialogComponent,
  ],
})
export class BillingUiModule {}
